import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import Navbar from '../components/Navbar';
import { useAuth } from '../services/auth/AuthContext';
import {
  getAppointments,
  getProviders,
  updateAppointment,
  cancelAppointment,
} from '../services/appointmentService';
import { deepPurple, green, grey, red } from '@mui/material/colors';

const statusOptions = [
  { value: 'scheduled', label: 'Scheduled', color: deepPurple[500] },
  { value: 'completed', label: 'Completed', color: green[600] },
  { value: 'cancelled', label: 'Cancelled', color: red[500] },
];

function formatDate(dateStr) {
  if (typeof dateStr !== 'string' || !dateStr.includes('-')) return dateStr || '—';
  const [y, m, d] = dateStr.split('-');
  if (y && m && d) return `${m}/${d}/${y}`;
  return dateStr;
}

function participantName(item) {
  return [item.first_name, item.last_name].filter(Boolean).join(' ') || '—';
}

export default function AppointmentList() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [providers, setProviders] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('appointment_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editAppointment, setEditAppointment] = useState(null);

  const handleCloseError = () => setError('');
  const handleCloseSuccess = () => setSuccess('');

  const providerIdToName = useMemo(() => {
    const map = {};
    (providers || []).forEach((p) => {
      map[p.user_id] = p.name || 'Provider';
    });
    return map;
  }, [providers]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      const [appResult, provResult] = await Promise.all([
        getAppointments(),
        getProviders(),
      ]);
      if (appResult.success) setAppointments(appResult.data || []);
      else setError(appResult.message || 'Failed to load appointments');
      if (provResult.success) setProviders(provResult.data || []);
      setLoading(false);
    };
    load();
  }, []);

  const filteredAppointments = useMemo(() => {
    return appointments.filter((apt) => {
      const name = participantName(apt).toLowerCase();
      const email = (apt.email || '').toLowerCase();
      const service = (apt.service || '').toLowerCase();
      const searchLower = search.toLowerCase();
      const matchesSearch =
        !searchLower ||
        name.includes(searchLower) ||
        email.includes(searchLower) ||
        service.includes(searchLower);
      const status = (apt.status || 'scheduled').toLowerCase();
      const matchesStatus = !statusFilter || status === statusFilter.toLowerCase();
      return matchesSearch && matchesStatus;
    });
  }, [appointments, search, statusFilter]);

  const sortedAppointments = useMemo(() => {
    return [...filteredAppointments].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      if (sortBy === 'appointment_date') {
        aVal = aVal ? new Date(aVal) : 0;
        bVal = bVal ? new Date(bVal) : 0;
      }
      if (sortBy === 'appointment_time') {
        aVal = aVal || '';
        bVal = bVal || '';
      }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredAppointments, sortBy, sortOrder]);

  const paginatedAppointments = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedAppointments.slice(start, start + rowsPerPage);
  }, [sortedAppointments, page, rowsPerPage]);

  const handleEdit = (apt) => {
    setEditAppointment({
      ...apt,
      appointment_date: apt.appointment_date || '',
    });
    setOpenEditDialog(true);
  };

  const handleUpdateAppointment = async () => {
    if (!editAppointment) return;
    const result = await updateAppointment(editAppointment.id, {
      provider_id: editAppointment.provider_id ?? null,
    });
    if (result.success) {
      setSuccess('Appointment updated.');
      setOpenEditDialog(false);
      setEditAppointment(null);
      const appResult = await getAppointments();
      if (appResult.success) setAppointments(appResult.data || []);
    } else {
      setError(result.message || 'Failed to update appointment');
    }
  };

  const handleDelete = async (apt) => {
    if (!window.confirm('Are you sure you want to cancel/delete this appointment?')) return;
    const result = await cancelAppointment(apt.id);
    if (result.success) {
      setSuccess('Appointment cancelled.');
      const appResult = await getAppointments();
      if (appResult.success) setAppointments(appResult.data || []);
    } else {
      setError(result.message || 'Failed to cancel appointment');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar />
      <Container maxWidth="lg" sx={{ pt: 10, mt: 4, mb: 6, flex: 1, pb: 8 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: deepPurple[800], mb: 3 }}>
            Scheduled Appointments
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'nowrap', alignItems: 'center' }}>
            <TextField
              label="Search"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              size="small"
              sx={{ mb: 0, flex: 1 }}
              fullWidth
            />
            <FormControl size="small" sx={{ minWidth: 180, flex: '0 0 220px' }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {statusOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Snackbar
            open={!!error}
            autoHideDuration={4000}
            onClose={handleCloseError}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          </Snackbar>
          <Snackbar
            open={!!success}
            autoHideDuration={2500}
            onClose={handleCloseSuccess}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
              {success}
            </Alert>
          </Snackbar>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: deepPurple[500] }} />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 520 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: deepPurple[50] }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        <TableSortLabel
                          active={sortBy === 'appointment_date'}
                          direction={sortBy === 'appointment_date' ? sortOrder : 'asc'}
                          onClick={() => {
                            setSortBy('appointment_date');
                            setSortOrder(
                              sortBy === 'appointment_date' && sortOrder === 'asc' ? 'desc' : 'asc'
                            );
                          }}
                        >
                          Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Service</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        <TableSortLabel
                          active={sortBy === 'status'}
                          direction={sortBy === 'status' ? sortOrder : 'asc'}
                          onClick={() => {
                            setSortBy('status');
                            setSortOrder(
                              sortBy === 'status' && sortOrder === 'asc' ? 'desc' : 'asc'
                            );
                          }}
                        >
                          Status
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Provider</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedAppointments.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell>{participantName(item)}</TableCell>
                        <TableCell>{item.email || '—'}</TableCell>
                        <TableCell>{formatDate(item.appointment_date)}</TableCell>
                        <TableCell>{item.appointment_time || '—'}</TableCell>
                        <TableCell>{item.service || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={(item.status || 'scheduled').toLowerCase()}
                            sx={{
                              backgroundColor: `${
                                statusOptions.find((o) => o.value === (item.status || 'scheduled').toLowerCase())
                                  ?.color || grey[500]
                              }20`,
                              color:
                                statusOptions.find((o) => o.value === (item.status || 'scheduled').toLowerCase())
                                  ?.color || grey[800],
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {item.provider_id != null
                            ? providerIdToName[item.provider_id] || `ID ${item.provider_id}`
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5}>
                            <Tooltip title="Edit">
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => handleEdit(item)}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete / Cancel">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleDelete(item)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={sortedAppointments.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </>
          )}

          {!loading && appointments.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No appointments found.
            </Typography>
          )}
        </Paper>
      </Container>

      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Appointment — Assign Provider</DialogTitle>
        <DialogContent dividers>
          {editAppointment && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label="First name"
                value={editAppointment.first_name || ''}
                disabled
              />
              <TextField
                fullWidth
                label="Last name"
                value={editAppointment.last_name || ''}
                disabled
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editAppointment.email || ''}
                disabled
              />
              <TextField
                fullWidth
                label="Phone"
                value={editAppointment.phone || ''}
                disabled
              />
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={editAppointment.appointment_date || ''}
                InputLabelProps={{ shrink: true }}
                disabled
              />
              <TextField
                fullWidth
                label="Time"
                value={editAppointment.appointment_time || ''}
                disabled
              />
              <TextField
                fullWidth
                label="Service"
                value={editAppointment.service || ''}
                disabled
              />
              <FormControl fullWidth>
                <InputLabel>Provider</InputLabel>
                <Select
                  label="Provider"
                  value={editAppointment.provider_id ?? ''}
                  onChange={(e) =>
                    setEditAppointment({
                      ...editAppointment,
                      provider_id: e.target.value === '' ? null : e.target.value,
                    })
                  }
                >
                  <MenuItem value="">None</MenuItem>
                  {providers.map((p) => (
                    <MenuItem key={p.user_id} value={p.user_id}>
                      {p.name || p.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Status"
                value={(editAppointment.status || 'scheduled').toLowerCase()}
                disabled
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateAppointment} sx={{ bgcolor: deepPurple[600] }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
